import { useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { useActivity } from '@/api/queries'
import { useAuth } from '@/api/auth'
import { useSyncStore } from '@/api/store'
import { unreadOf } from '@/api/types'
import type { ActivityEntry, Message, Room } from '@/api/types'
import { Avatar, Button, EmptyState, Spinner } from '@/components/ui'
import { roomLabel, useRoomLabels } from '@/components/Sidebar'
import { formatDay, formatTime } from '@/lib/format'
import styles from './ActivityPage.module.css'
import { useAppLayout } from './AppShell'

function senderName(message: Message): string {
  const override = message.sender_override?.trim() ?? ''
  if (override !== '') return override
  return message.sender.display_name.trim() || message.sender.username
}

function entryText(message: Message): string {
  const text = message.text.replace(/\s+/gu, ' ').trim()
  if (text !== '') return text
  const files = message.attachments.length
  return files === 1 ? 'Shared a file' : `Shared ${files} files`
}

function destination(entry: ActivityEntry): string {
  const params = new URLSearchParams({ message: entry.message.id })
  if (entry.message.parent_id) params.set('thread', entry.message.parent_id)
  return `/rooms/${encodeURIComponent(entry.room.id)}?${params.toString()}`
}

function displayRoom(room: Room, labels: ReadonlyMap<string, string>): string {
  const label = roomLabel(room, labels)
  return room.kind === 'channel' ? `#${label}` : label
}

export function ActivityPage({ embedded = false }: { embedded?: boolean }): ReactElement {
  const { server } = useAuth()
  const navigate = useNavigate()
  const layout = useAppLayout()
  const labels = useRoomLabels()
  const { rooms, cursor } = useSyncStore()
  const [filter, setFilter] = useState<'all' | 'mentions' | 'threads'>('all')
  const activity = useActivity(50, filter)

  const unreadTotal = useMemo(
    () =>
      rooms.reduce((total, room) => {
        const unread = unreadOf(room)
        return total + Math.max(unread.count, unread.mentions)
      }, 0),
    [rooms],
  )

  // A socket event updates the room counters immediately. Refresh the cross-
  // room page against the same server-side watermarks while it is visible.
  useEffect(() => {
    void activity.refetch()
    // `refetch` is stable; unreadTotal is the event signal this view follows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadTotal, cursor])

  const entries = activity.data ?? []
  const webhookEntries = entries.filter((entry) => entry.message.sender_override !== null).slice(0, 8)

  return (
    <section className={styles.page} aria-labelledby="activity-title">
      {!embedded ? <header className={styles.header}>
        <div>
          <h1 className={styles.title} id="activity-title">
            <span className={styles.titleIcon} aria-hidden="true">◷</span>
            Activity
          </h1>
        </div>
        {entries.length > 0 ? (
          <span className={styles.total}>{entries.length} unread</span>
        ) : null}
      </header> : null}

      <div className={styles.body}>
        {server?.capabilities?.activity_filters === true ? <div role="group" aria-label="Activity filter">
          {(['all', 'mentions', 'threads'] as const).map((value) => <Button key={value} variant="ghost" aria-pressed={filter === value} onClick={() => setFilter(value)}>
            {value === 'all' ? 'All unread' : value === 'mentions' ? 'Mentions' : 'My threads'}
          </Button>)}
        </div> : null}
        {activity.isPending ? (
          <div className={styles.centered}>
            <Spinner size="lg" label="Loading activity" />
          </div>
        ) : activity.isError ? (
          <EmptyState
            title="Activity could not be loaded"
            description={activity.error?.message ?? 'Try again in a moment.'}
            action={<Button onClick={() => void activity.refetch()}>Try again</Button>}
          />
        ) : entries.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>You are all caught up</h2>
            <p>New messages from your rooms and threads will appear here.</p>
          </div>
        ) : (
          <ul className={styles.list}>
            {entries.map((entry, index) => {
              const previous = entries[index - 1]
              const day = formatDay(entry.message.created_at)
              const showDay = previous === undefined || formatDay(previous.message.created_at) !== day
              const name = senderName(entry.message)

              return (
                <li key={entry.message.id} className={styles.item}>
                  {showDay ? <h2 className={styles.day}>{day}</h2> : null}
                  <button
                    type="button"
                    className={styles.entry}
                    onClick={() => navigate(destination(entry))}
                  >
                    <Avatar
                      name={name}
                      id={entry.message.sender.id}
                      src={entry.message.sender_avatar_url ?? entry.message.sender.avatar_url}
                      size="sm"
                      decorative
                    />
                    <span className={styles.content}>
                      <span className={styles.meta}>
                        <strong>{name}</strong>
                        <span className={styles.room}>{displayRoom(entry.room, labels)}</span>
                        {entry.message.parent_id ? (
                          <span className={styles.thread}>in a thread</span>
                        ) : null}
                        <time dateTime={entry.message.created_at}>
                          {formatTime(entry.message.created_at)}
                        </time>
                      </span>
                      <span className={styles.text}>{entryText(entry.message)}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {activity.hasNextPage ? (
          <div className={styles.more}>
            <Button
              loading={activity.isFetchingNextPage}
              onClick={() => void activity.fetchNextPage()}
            >
              Load earlier activity
            </Button>
          </div>
        ) : null}
      </div>
      {!embedded && layout.detailsOpen && layout.detailsHost !== null
        ? createPortal(
            <section className={styles.webhookPanel} aria-labelledby="webhook-activity-title">
              <header className={styles.webhookHeader}>
                <h2 id="webhook-activity-title">
                  <span className={styles.webhookIcon} aria-hidden="true">↗</span>
                  Webhook activity
                </h2>
              </header>
              <div className={styles.webhookBody}>
                {webhookEntries.length === 0 ? (
                  <p>No webhook activity yet.</p>
                ) : (
                  <ul>
                  {webhookEntries.map((entry) => (
                    <li key={entry.message.id}>
                      <strong>{entry.message.sender_override}</strong>
                      <span>{entry.room.name ?? entry.room.slug ?? 'Conversation'}</span>
                      <time dateTime={entry.message.created_at}>{formatTime(entry.message.created_at)}</time>
                    </li>
                  ))}
                  </ul>
                )}
              </div>
            </section>,
            layout.detailsHost,
          )
        : null}
    </section>
  )
}

export default ActivityPage
